import { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useMall } from "@/context/MallContext";
import MallMap from "@/components/mall/map/MallMap";
import UnitPopup from "@/components/mall/map/UnitPopup";
import { MALL_ROOMS, buildPlan } from "@/components/mall/map/floorplan";
import { ShopList } from "@/components/directory/ShopList";
import { markSource } from "@/lib/source";

/** Click a shop on the map or in the list and you're on its page. Rooms on the map are the menu. */
export const usePlanPick = (plan) => {
    const { enter } = useMall();
    const navigate = useNavigate();
    const [unit, setUnit] = useState(null);
    const pick = (slot) => {
        if (slot.kind === "room") { navigate(MALL_ROOMS[slot.room].to); return; }
        if (slot.store) {
            markSource("map");
            enter(slot.store, "map");
            navigate(`/stores/${slot.store.slug}`);
            return;
        }
        setUnit(slot);
    };
    return { pick, unit, closeUnit: () => setUnit(null) };
};

/**
 * The directory, top to bottom: what this page is, the list of shops on the left, the map on the
 * right. The map stays put while the list scrolls.
 */
export default function DirectoryHero() {
    const { floor } = useMall();
    const [hoverId, setHoverId] = useState(null);
    const plan = useMemo(() => buildPlan(floor), [floor]);
    const { pick, unit, closeUnit } = usePlanPick(plan);
    const hover = (slot) => setHoverId(slot ? slot.id : null);
    const hoverSlug = (slug) => {
        if (!slug) return setHoverId(null);
        const slot = plan.slots.find((s) => s.store?.slug === slug) || (plan.pads || []).find((p) => p.store?.slug === slug);
        setHoverId(slot ? slot.id : null);
    };
    const shops = floor ? ["own_sites", "anchors", "storefronts", "kiosks"].reduce((n, k) => n + (floor[k] || []).length, 0) : 0;

    return (
        <section className="relative border-b border-zinc-200 bg-white" data-testid="directory-hero">
            <div className="mx-auto grid max-w-[96rem] gap-8 px-4 py-8 sm:px-8 lg:grid-cols-5 lg:items-start lg:gap-10">
                <div className="lg:col-span-2">
                    <p className="font-mono text-[11px] uppercase tracking-[0.3em] text-zinc-500">13thav directory</p>
                    <h1 className="mt-3 font-display text-4xl font-extrabold leading-[0.95] tracking-tight text-zinc-900 sm:text-5xl" data-testid="directory-title">
                        {shops || ""} print shops. Order online from any of them.
                    </h1>
                    <p className="mt-3 max-w-md text-base text-zinc-600" data-testid="directory-sub">
                        Click a shop to see its prices, its pickup rules and its billboards. Or start with what you need printed, below.
                    </p>
                    <div className="mt-6">
                        <ShopList floor={floor} plan={plan} onHover={hoverSlug} />
                    </div>
                </div>

                <div className="lg:sticky lg:top-24 lg:col-span-3 lg:h-[calc(100vh-7.5rem)]">
                    <MallMap plan={plan} hoverId={hoverId} onHover={hover} onPick={pick} compact />
                </div>
            </div>

            {unit && <UnitPopup slot={unit} onClose={closeUnit} />}
        </section>
    );
}
